import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { useLanguage } from "../contexts/LanguageContext";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import useExperiment from "../hooks/useExperiment";
import {
  Sparkles,
  ArrowLeft,
  Briefcase,
  MapPin,
  TrendingUp,
  Lightbulb,
  User,
  ChevronRight,
  Star,
  Zap,
  Target,
  Award,
  RefreshCw,
  ThumbsUp,
  ThumbsDown,
  Bookmark,
  ExternalLink,
  Filter,
  Users,
  CheckCircle,
  AlertCircle,
  BookOpen,
  GraduationCap,
  Play,
  Clock,
  FlaskConical,
  Loader2,
  ClipboardCheck
} from "lucide-react";

const ForYou = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState("");
  const [skillCourses, setSkillCourses] = useState(null);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [enrichmentSummary, setEnrichmentSummary] = useState(null);
  const [expandedExplain, setExpandedExplain] = useState(null);
  const [explainLoading, setExplainLoading] = useState(null);
  const [explainData, setExplainData] = useState({});
  
  // A/B Testing integration for job recommendations
  const { 
    variant: experimentVariant, 
    config: experimentConfig, 
    trackConversion,
    isInExperiment,
    loading: experimentLoading 
  } = useExperiment("job_recommendations");

  useEffect(() => {
    fetchRecommendations();
  }, [selectedJobId]);

  // Fetch skill courses when recommendations load (for job seekers)
  useEffect(() => {
    if (recommendations && user?.role === "jobseeker") {
      fetchSkillCourses();
    }
  }, [recommendations]);

  const fetchSkillCourses = async () => {
    setCoursesLoading(true);
    try {
      // Get missing skills from recommendations
      const missingSkills = [];
      recommendations?.recommendations?.forEach(rec => {
        if (rec.match_explanation?.skills_to_learn) {
          missingSkills.push(...rec.match_explanation.skills_to_learn);
        }
      });
      
      const uniqueSkills = [...new Set(missingSkills)].slice(0, 5);
      const skillsParam = uniqueSkills.length > 0 ? `?skills=${uniqueSkills.join(",")}` : "";
      
      const response = await fetch(`${API}/recommendations/skill-courses${skillsParam}`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setSkillCourses(data);
      }
    } catch (error) {
      console.error("Failed to fetch skill courses:", error);
    } finally {
      setCoursesLoading(false);
    }
  };

  const trackCourseClick = async (course) => {
    try {
      await fetch(`${API}/recommendations/skill-courses/click`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          course_title: course.title,
          provider: course.provider,
          skill: course.for_skill
        })
      });
    } catch (error) {
      console.error("Failed to track click:", error);
    }
  };

  const fetchRecommendations = async () => {
    setLoading(true);
    try {
      let endpoint;
      if (user?.role === "recruiter") {
        const params = new URLSearchParams();
        if (selectedJobId) params.append("job_id", selectedJobId);
        params.append("limit", "20");
        endpoint = `/recommendations/candidates?${params.toString()}`;
      } else {
        // Include experiment variant in the request for tracking
        const params = new URLSearchParams();
        params.append("limit", "20");
        if (experimentVariant && experimentVariant !== "control") {
          params.append("variant", experimentVariant);
        }
        endpoint = `/recommendations/jobs?${params.toString()}`;
      }
      
      const response = await fetch(`${API}${endpoint}`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecommendations(data);
        // Store jobs for filter dropdown
        if (data.jobs) {
          setJobs(data.jobs);
        }
      } else {
        toast.error("Failed to load recommendations");
      }

      // Also fetch AI-enhanced matches for jobseekers
      if (user?.role === "jobseeker") {
        try {
          const aiRes = await fetch(`${API}/matching/jobs/ai-enhanced?limit=15`, { credentials: "include" });
          if (aiRes.ok) {
            const aiData = await aiRes.json();
            setEnrichmentSummary(aiData.enrichment_summary || null);
          }
        } catch {}
      }
    } catch (error) {
      console.error("Failed to fetch recommendations:", error);
      toast.error("Failed to load recommendations");
    } finally {
      setLoading(false);
    }
  };

  // Track conversion when user clicks to view a job (indicates interest)
  const handleJobClick = async (jobId) => {
    if (isInExperiment) {
      await trackConversion("job_clicked", { jobId });
    }
    navigate(`/jobs/${jobId}`);
  };

  // Track conversion when user applies to a job
  const handleApplyClick = async (jobId) => {
    if (isInExperiment) {
      await trackConversion("job_applied", { jobId });
    }
  };

  const submitFeedback = async (itemId, action) => {
    try {
      await fetch(`${API}/recommendations/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          type: user?.role === "recruiter" ? "candidate" : "job",
          item_id: itemId,
          action: action
        })
      });
      toast.success(action === "interested" ? "Saved!" : "Got it, we'll show fewer like this");
    } catch (error) {
      console.error("Failed to submit feedback:", error);
    }
  };

  const handleExplainMatch = async (jobId) => {
    if (expandedExplain === jobId) {
      setExpandedExplain(null);
      return;
    }
    setExpandedExplain(jobId);
    if (explainData[jobId]) return; // already loaded
    setExplainLoading(jobId);
    try {
      const res = await fetch(`${API}/matching/jobs/${jobId}/explain`, {
        method: "POST",
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setExplainData((prev) => ({ ...prev, [jobId]: data.explanation }));
      } else {
        toast.error("Could not load explanation");
      }
    } catch {
      toast.error("Failed to get explanation");
    } finally {
      setExplainLoading(null);
    }
  };

  const dashboardUrl = user?.role === "recruiter" ? "/dashboard/recruiter" : "/dashboard/jobseeker";
  const isRecruiter = user?.role === "recruiter";

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex">
        <Sidebar />
        <main className="flex-1 lg:ml-64 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-2 border-crimson border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Analyzing candidates...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian flex">
      <Sidebar />

      <main className="flex-1 lg:ml-64 p-4 sm:p-6 lg:p-8 pt-20 lg:pt-8">
        <div className="max-w-5xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-crimson to-orange-500 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-xl sm:text-2xl font-bold">
                  {isRecruiter ? "RECOMMENDED APPLICANTS" : "FOR YOU"}
                </h1>
                <p className="text-gray-400 text-xs sm:text-sm">
                  {isRecruiter 
                    ? `${recommendations?.total_applicants || 0} applicants ranked by match score`
                    : "AI-powered job recommendations"
                  }
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Job Filter for Recruiters */}
              {isRecruiter && jobs.length > 0 && (
                <select
                  value={selectedJobId}
                  onChange={(e) => setSelectedJobId(e.target.value)}
                  className="px-4 py-2 bg-obsidian border border-[#333] text-white focus:border-crimson focus:outline-none"
                  data-testid="job-filter"
                >
                  <option value="">All Jobs ({jobs.length})</option>
                  {jobs.map((job) => (
                    <option key={job.job_id} value={job.job_id}>
                      {job.title}
                    </option>
                  ))}
                </select>
              )}
              
              <Button
                onClick={fetchRecommendations}
                variant="outline"
                className="border-[#333] text-white"
                data-testid="refresh-recommendations-btn"
              >
                <RefreshCw className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            </div>
          </div>
          {/* AI Insight */}
          {recommendations?.insights && (
            <motion.div
              className="bg-gradient-to-r from-crimson/20 to-orange-500/20 border border-crimson/30 p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-crimson/20 flex items-center justify-center flex-shrink-0">
                  <Lightbulb className="w-5 h-5 text-crimson" />
                </div>
                <div className="flex-1">
                  <h2 className="font-heading text-lg font-bold mb-2">AI Career Insight</h2>
                  <p className="text-gray-300 leading-relaxed">{recommendations.insights}</p>
                </div>
                {/* A/B Test Indicator (visible in demo) */}
                {isInExperiment && !isRecruiter && (
                  <div className="flex items-center gap-2 px-3 py-1 bg-purple-500/20 border border-purple-500/30 text-xs text-purple-300" data-testid="ab-test-indicator">
                    <FlaskConical className="w-3 h-3" />
                    <span>Testing: {experimentVariant}</span>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Enrichment Summary for Jobseekers */}
          {!isRecruiter && enrichmentSummary && (enrichmentSummary.assessments_taken > 0 || enrichmentSummary.interview_sessions > 0) && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-obsidian-light border border-[#222] p-3 rounded" data-testid="enrichment-assessments">
                <div className="flex items-center gap-2 mb-1">
                  <ClipboardCheck className="w-4 h-4 text-emerald-400" />
                  <span className="text-xs text-gray-400">Assessments</span>
                </div>
                <p className="text-lg font-bold">{enrichmentSummary.assessments_passed}/{enrichmentSummary.assessments_taken}</p>
                <p className="text-xs text-gray-500">passed</p>
              </div>
              <div className="bg-obsidian-light border border-[#222] p-3 rounded" data-testid="enrichment-badges">
                <div className="flex items-center gap-2 mb-1">
                  <Award className="w-4 h-4 text-amber-400" />
                  <span className="text-xs text-gray-400">Badges</span>
                </div>
                <p className="text-lg font-bold">{enrichmentSummary.badge_count}</p>
                <p className="text-xs text-gray-500">earned</p>
              </div>
              <div className="bg-obsidian-light border border-[#222] p-3 rounded" data-testid="enrichment-interviews">
                <div className="flex items-center gap-2 mb-1">
                  <Play className="w-4 h-4 text-blue-400" />
                  <span className="text-xs text-gray-400">Mock Interviews</span>
                </div>
                <p className="text-lg font-bold">{enrichmentSummary.interview_sessions}</p>
                <p className="text-xs text-gray-500">{enrichmentSummary.avg_interview_score > 0 ? `${enrichmentSummary.avg_interview_score}% avg` : "sessions"}</p>
              </div>
              <div className="bg-obsidian-light border border-[#222] p-3 rounded" data-testid="enrichment-score">
                <div className="flex items-center gap-2 mb-1">
                  <Target className="w-4 h-4 text-crimson" />
                  <span className="text-xs text-gray-400">Avg Score</span>
                </div>
                <p className="text-lg font-bold">{enrichmentSummary.avg_assessment_score}%</p>
                <p className="text-xs text-gray-500">assessments</p>
              </div>
            </div>
          )}

          {/* Recommendations List */}
          <div className="space-y-4">
            <h2 className="font-heading text-lg font-bold flex items-center gap-2">
              <Users className="w-5 h-5 text-crimson" />
              {isRecruiter ? "Applicants Ranked by Match" : "Recommended For You"}
            </h2>

            {recommendations?.recommendations?.length === 0 ? (
              <div className="bg-obsidian-light border border-[#222] p-12 text-center">
                <Briefcase className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No recommendations available yet.</p>
                <p className="text-gray-500 text-sm mt-2">
                  {isRecruiter 
                    ? "Post jobs and wait for applicants to get recommendations"
                    : "Complete your profile to get personalized job recommendations"}
                </p>
              </div>
            ) : (
              recommendations?.recommendations?.map((item, i) => (
                <motion.div
                  key={item.job_id || item.user_id}
                  className="bg-obsidian-light border border-[#222] p-6 hover:border-[#333] transition-colors"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.03 }}
                  data-testid={`recommendation-${i}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {/* Avatar for candidates */}
                        {isRecruiter && (
                          <img
                            src={item.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(item.name || "U")}&background=FF2A2A&color=fff`}
                            alt={item.name}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        )}
                        <div>
                          <h3 className="font-heading text-lg font-bold text-white">
                            {item.title || item.name}
                          </h3>
                          {item.headline && (
                            <p className="text-gray-400 text-sm">{item.headline}</p>
                          )}
                        </div>
                        {item.is_trending && (
                          <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs flex items-center gap-1">
                            <Zap className="w-3 h-3" /> Trending
                          </span>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-3 text-gray-400 text-sm mb-3">
                        {item.company && (
                          <span className="flex items-center gap-1">
                            <Briefcase className="w-4 h-4" /> {item.company}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" /> {item.location}
                        </span>
                        {item.salary_range && (
                          <span className="text-green-400">{item.salary_range}</span>
                        )}
                        {item.jobs_applied_count && (
                          <span className="text-blue-400">
                            Applied to {item.jobs_applied_count} job{item.jobs_applied_count > 1 ? "s" : ""}
                          </span>
                        )}
                      </div>

                      {/* Match Score with Explanation */}
                      <div className="flex items-center gap-3 mb-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold ${
                            item.match_score >= 80 ? "bg-green-500/20 text-green-400 border border-green-500/30" :
                            item.match_score >= 60 ? "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30" :
                            "bg-gray-500/20 text-gray-400 border border-gray-500/30"
                          }`}>
                            {item.match_score}%
                          </div>
                          <div className="flex flex-col">
                            <span className="text-gray-400 text-sm">Match Score</span>
                            {/* Personalized skill match explanation */}
                            {item.match_explanation?.skill_match_text && (
                              <span className={`text-xs flex items-center gap-1 ${
                                item.match_explanation.skill_match_percent >= 70 ? "text-green-400" :
                                item.match_explanation.skill_match_percent >= 50 ? "text-yellow-400" :
                                "text-gray-500"
                              }`} data-testid={`skill-match-${item.job_id}`}>
                                <CheckCircle className="w-3 h-3" />
                                {item.match_explanation.skill_match_text}
                              </span>
                            )}
                          </div>
                        </div>
                        {item.applicant_count !== undefined && (
                          <span className="text-gray-500 text-sm">
                            {item.applicant_count} applicants
                          </span>
                        )}
                        {item.experience_count !== undefined && (
                          <span className="text-gray-500 text-sm">
                            {item.experience_count} years exp
                          </span>
                        )}
                      </div>

                      {/* Matching Skills */}
                      {item.matching_skills?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-gray-500 text-xs mb-1 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3 text-green-400" />
                            Your Matching Skills:
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {item.matching_skills.slice(0, 5).map((skill) => (
                              <span key={skill} className="px-2 py-0.5 bg-green-500/10 text-green-400 text-xs">
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Skills to Learn - only for job seekers */}
                      {!isRecruiter && item.match_explanation?.skills_to_learn?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-gray-500 text-xs mb-1 flex items-center gap-1">
                            <BookOpen className="w-3 h-3 text-yellow-400" />
                            Skills to learn for this role:
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {item.match_explanation.skills_to_learn.map((skill) => (
                              <span key={skill} className="px-2 py-0.5 bg-yellow-500/10 text-yellow-400 text-xs border border-yellow-500/20">
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Why Recommended - Multiple Reasons */}
                      <div className="space-y-1">
                        {item.reasons?.slice(0, 3).map((reason, idx) => (
                          <p key={idx} className="text-gray-400 text-sm flex items-center gap-2">
                            {idx === 0 ? <Award className="w-4 h-4 text-crimson flex-shrink-0" /> : 
                             <span className="w-4 h-4 flex items-center justify-center text-gray-600">•</span>}
                            {reason}
                          </p>
                        ))}
                      </div>

                      {/* AI Match Explanation */}
                      {!isRecruiter && item.job_id && (
                        <div className="mt-3">
                          <button
                            onClick={() => handleExplainMatch(item.job_id)}
                            className="text-xs text-crimson hover:underline flex items-center gap-1"
                            data-testid={`explain-match-${item.job_id}`}
                          >
                            <Lightbulb className="w-3 h-3" />
                            {expandedExplain === item.job_id ? "Hide" : "Why this match?"}
                            {explainLoading === item.job_id && <Loader2 className="w-3 h-3 animate-spin ml-1" />}
                          </button>
                          {expandedExplain === item.job_id && explainData[item.job_id] && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              className="mt-2 bg-obsidian border border-[#333] rounded p-3 space-y-2 text-sm"
                            >
                              <p className="text-gray-300">{explainData[item.job_id].summary}</p>
                              {explainData[item.job_id].strengths?.length > 0 && (
                                <div>
                                  <span className="text-xs text-emerald-400 font-medium">Strengths: </span>
                                  <span className="text-xs text-gray-400">{explainData[item.job_id].strengths.join(" · ")}</span>
                                </div>
                              )}
                              {explainData[item.job_id].gaps?.length > 0 && (
                                <div>
                                  <span className="text-xs text-amber-400 font-medium">Gaps: </span>
                                  <span className="text-xs text-gray-400">{explainData[item.job_id].gaps.join(" · ")}</span>
                                </div>
                              )}
                              {explainData[item.job_id].action_items?.length > 0 && (
                                <div>
                                  <span className="text-xs text-blue-400 font-medium">Actions: </span>
                                  <span className="text-xs text-gray-400">{explainData[item.job_id].action_items.join(" · ")}</span>
                                </div>
                              )}
                            </motion.div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex sm:flex-col gap-2">
                      <Button
                        onClick={() => {
                          if (isRecruiter) {
                            navigate(`/candidates/${item.user_id}`);
                          } else {
                            // Track conversion for A/B test
                            handleJobClick(item.job_id);
                          }
                        }}
                        className="bg-crimson hover:bg-crimson/90 text-white flex-1 sm:flex-none"
                        data-testid={`view-${item.job_id || item.user_id}`}
                      >
                        View <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                      <div className="flex gap-1">
                        <button
                          onClick={() => {
                            submitFeedback(item.job_id || item.user_id, "interested");
                            // Track interest as a conversion signal
                            if (!isRecruiter && isInExperiment) {
                              trackConversion("job_interested", { jobId: item.job_id });
                            }
                          }}
                          className="p-2 bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors"
                          title="Interested"
                        >
                          <ThumbsUp className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => submitFeedback(item.job_id || item.user_id, "not_interested")}
                          className="p-2 bg-gray-500/10 text-gray-400 hover:bg-gray-500/20 transition-colors"
                          title="Not interested"
                        >
                          <ThumbsDown className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>

          {/* Skill Courses Section - For Job Seekers Only */}
          {!isRecruiter && skillCourses?.courses?.length > 0 && (
            <motion.div
              className="mt-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="font-heading text-lg font-bold uppercase tracking-wider" data-testid="skill-courses-header">
                      Upskill for Your Dream Job
                    </h2>
                    <p className="text-gray-500 text-sm">Courses tailored to your career goals and skill gaps</p>
                  </div>
                </div>
              </div>

              {/* Missing Skills Pills - Based on profile analysis */}
              {skillCourses.missing_skills?.length > 0 && (
                <div className="bg-obsidian-light border border-[#222] p-4 mb-4">
                  <p className="text-gray-400 text-sm mb-3">
                    Based on your profile and the jobs you're interested in, we recommend learning:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {skillCourses.missing_skills.map((skill) => (
                      <span
                        key={skill}
                        className="px-3 py-1 bg-purple-500/20 text-purple-400 text-sm border border-purple-500/30"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Course Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {skillCourses.courses.map((course, index) => (
                  <motion.a
                    key={`${course.title}-${index}`}
                    href={course.affiliate_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackCourseClick(course)}
                    className="group bg-obsidian-light border border-[#222] hover:border-purple-500/50 transition-all duration-300 overflow-hidden"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * index }}
                    data-testid={`course-card-${index}`}
                  >
                    {/* Course Image */}
                    <div className="relative h-32 overflow-hidden">
                      <img
                        src={course.image}
                        alt={course.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.src = "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400";
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-obsidian-light to-transparent" />
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-0.5 bg-purple-500/90 text-white text-xs font-medium">
                          {course.for_skill}
                        </span>
                      </div>
                      <div className="absolute top-2 right-2">
                        <span className={`px-2 py-0.5 text-xs font-medium ${
                          course.price === "Free" 
                            ? "bg-green-500/90 text-white" 
                            : "bg-obsidian/80 text-white"
                        }`}>
                          {course.price}
                        </span>
                      </div>
                    </div>

                    {/* Course Info */}
                    <div className="p-4">
                      <h3 className="font-medium text-white text-sm mb-1 line-clamp-2 group-hover:text-purple-400 transition-colors">
                        {course.title}
                      </h3>
                      <p className="text-gray-500 text-xs mb-2">{course.provider}</p>

                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                          {course.rating}
                        </span>
                        <span>({course.reviews})</span>
                      </div>

                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {course.duration}
                        </span>
                        <span className={`px-1.5 py-0.5 ${
                          course.level === "Beginner" ? "bg-green-500/20 text-green-400" :
                          course.level === "Intermediate" ? "bg-yellow-500/20 text-yellow-400" :
                          course.level === "Advanced" ? "bg-red-500/20 text-red-400" :
                          "bg-blue-500/20 text-blue-400"
                        }`}>
                          {course.level}
                        </span>
                      </div>

                      {/* CTA */}
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-crimson text-sm font-medium group-hover:underline flex items-center gap-1">
                          Start Learning
                          <ExternalLink className="w-3 h-3" />
                        </span>
                      </div>
                    </div>
                  </motion.a>
                ))}
              </div>

              {/* Affiliate Disclaimer */}
              {skillCourses.disclaimer && (
                <p className="text-gray-600 text-xs mt-4 text-center">
                  {skillCourses.disclaimer}
                </p>
              )}
            </motion.div>
          )}

          {/* Courses Loading State */}
          {!isRecruiter && coursesLoading && (
            <div className="mt-8 flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mr-3" />
              <span className="text-gray-500">Loading recommended courses...</span>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ForYou;
